'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle, BookMarked, Check, CheckCircle2, ChevronLeft, Copy,
  Loader2, MoreVertical, Pencil, Plus, Trash2, X,
} from 'lucide-react';
import {
  Subject, Teacher, SectionSubjectRow,
  updateSchoolClass, createSection, updateSection, deleteSection,
  getSectionSubjects, assignSectionSubjectTeacher, assignSectionTeachers,
  cloneSectionTeachers, assignSubjectToClass,
} from '@/actions/data-fetch-curriculum';
import { toastMessage } from '@/utils/toast-message';
import { CARD_CATS, type SidePanelType } from './curriculum-types';
import { SideDrawer, TeacherPickerList, SubjectPickerPanel, TemplatePickerPanel } from './curriculum-components';

// ─── Props ────────────────────────────────────────────────────────────────────
interface ClassWorkspaceProps {
  selectedClass: any;
  selectedClassId: number;
  activeCurriculum: any | null;
  sections: any[];
  subjectMaps: any[];
  subjectMaster: Subject[];
  teachers: Teacher[];
  closeModal: () => void;
  onReloadData: (silent?: boolean) => Promise<void>;
  onReloadSubjectMaster: () => Promise<void>;
}

// ─── ClassWorkspace ───────────────────────────────────────────────────────────
export const ClassWorkspace: React.FC<ClassWorkspaceProps> = ({
  selectedClass, selectedClassId, activeCurriculum, sections, subjectMaps,
  subjectMaster, teachers, closeModal, onReloadData, onReloadSubjectMaster,
}) => {
  const [sidePanel, setSidePanel] = useState<SidePanelType | null>(null);
  const [openKebab, setOpenKebab] = useState<number | null>(null);
  const [openKebabPos, setOpenKebabPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const [openCtKebab, setOpenCtKebab] = useState<number | null>(null);
  const [openCtKebabPos, setOpenCtKebabPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const [editingWorkspaceClassName, setEditingWorkspaceClassName] = useState(false);
  const [workspaceClassNameVal, setWorkspaceClassNameVal] = useState('');
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [editingSectionLabel, setEditingSectionLabel] = useState('');
  const [newSectionLabel, setNewSectionLabel] = useState('');
  const [cloneFromSectionId, setCloneFromSectionId] = useState<number | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<number | null>(
    sections.length > 0 ? sections[0].id : null
  );
  const [sectionSubjects, setSectionSubjects] = useState<SectionSubjectRow[]>([]);
  const [loadingSectionSubjects, setLoadingSectionSubjects] = useState(false);
  const [savingTeacher, setSavingTeacher] = useState<string | null>(null);

  const loadSectionSubjects = useCallback(async (sectionId: number) => {
    setLoadingSectionSubjects(true);
    try {
      const res = await getSectionSubjects(sectionId);
      if (res.success) setSectionSubjects(res.data ?? []);
    } catch (e: any) {
      toastMessage(e.message || 'Failed to load subjects', 'error');
    } finally { setLoadingSectionSubjects(false); }
  }, []);

  // Load subjects on active section change
  useEffect(() => {
    if (activeSectionId) loadSectionSubjects(activeSectionId);
    else setSectionSubjects([]);
  }, [activeSectionId]);

  // Keep activeSectionId valid when sections change
  useEffect(() => {
    if (sections.length === 0) {
      setActiveSectionId(null);
      setSectionSubjects([]);
    } else if (!activeSectionId || !sections.find((s: any) => s.id === activeSectionId)) {
      setActiveSectionId(sections[0].id);
    }
  }, [sections.length]);

  // Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (sidePanel) { setSidePanel(null); return; }
        closeModal();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [sidePanel, closeModal]);

  // ── Progress ──────────────────────────────────────────────────────────────
  const computeProgress = () => {
    const hasSubjects = subjectMaps.length > 0;
    const allSectionsHaveClassTeacher = sections.length === 0 || sections.every((s: any) => s.primary_teacher_id);
    const allSectionSubjectsHaveTeachers = sections.length === 0 ||
      sections.every((sec: any) => {
        const maps = sec.PaSectionSubjectMaps ?? [];
        return subjectMaps.length === 0 || (maps.length >= subjectMaps.length && maps.every((m: any) => m.teacher_id_fk));
      });
    const reqs = [sections.length > 0, hasSubjects, allSectionSubjectsHaveTeachers && hasSubjects, allSectionsHaveClassTeacher];
    const done = reqs.filter(Boolean).length;
    return { reqs, done, pct: Math.round((done / reqs.length) * 100) };
  };

  // ── Class rename ──────────────────────────────────────────────────────────
  const handleRenameClass = async (name: string) => {
    const res = await updateSchoolClass(selectedClassId, { name });
    if (res?.success) { toastMessage('Class renamed', 'success'); setEditingWorkspaceClassName(false); await onReloadData(true); }
  };

  // ── Section actions ───────────────────────────────────────────────────────
  const handleAddSection = async () => {
    if (!newSectionLabel.trim()) return;
    const payload: { class_id: number; section_label: string; clone_from_section_id?: number } = {
      class_id: selectedClassId, section_label: newSectionLabel.trim(),
    };
    if (cloneFromSectionId) payload.clone_from_section_id = cloneFromSectionId;
    const res = await createSection(payload);
    if (res?.success) {
      setNewSectionLabel(''); setCloneFromSectionId(null); setSidePanel(null);
      await onReloadData(true);
      const newSec = res.data;
      if (newSec?.id) { setActiveSectionId(newSec.id); await loadSectionSubjects(newSec.id); }
    } else {
      toastMessage((res as any)?.message || 'Section may already exist', 'error');
    }
  };

  const handleRenameSection = async (id: number, label: string) => {
    const res = await updateSection(id, { section_label: label });
    if (res?.success) { setEditingSectionId(null); await onReloadData(true); }
  };

  const handleDeleteSection = async (id: number) => {
    if (!confirm('Delete this section and all its teacher assignments?')) return;
    const res = await deleteSection(id);
    if (res?.success) {
      toastMessage('Section deleted', 'success');
      await onReloadData(true);
      if (activeSectionId === id) {
        const remaining = sections.filter((s: any) => s.id !== id);
        if (remaining.length > 0) { setActiveSectionId(remaining[0].id); await loadSectionSubjects(remaining[0].id); }
        else { setActiveSectionId(null); setSectionSubjects([]); }
      }
    }
  };

  // ── Section-subject teacher ───────────────────────────────────────────────
  const handleAssignSectionTeacher = async (subjectId: number, teacherId: number | null) => {
    if (!activeSectionId) return;
    const key = `ssm-${activeSectionId}-${subjectId}`;
    setSavingTeacher(key);
    try {
      const res = await assignSectionSubjectTeacher(activeSectionId, subjectId, teacherId);
      if (res?.success) { await loadSectionSubjects(activeSectionId); await onReloadData(true); setSidePanel(null); }
      else toastMessage((res as any)?.message || 'Failed', 'error');
    } catch (e: any) {
      toastMessage(e.message || 'Failed', 'error');
    } finally { setSavingTeacher(null); }
  };

  // ── Class teacher ─────────────────────────────────────────────────────────
  const handleSectionClassTeacher = async (section: any, teacherId: number | null, role: 'primary' | 'secondary') => {
    const key = `ct-${section.id}-${role}`;
    setSavingTeacher(key);
    try {
      const primary = role === 'primary' ? teacherId : (section.primary_teacher_id ?? null);
      const secondary = role === 'secondary' ? teacherId : (section.secondary_teacher_id ?? null);
      await assignSectionTeachers(section.id, primary, secondary);
      await onReloadData(true); setSidePanel(null);
    } catch (e: any) {
      toastMessage(e.message || 'Failed', 'error');
    } finally { setSavingTeacher(null); }
  };

  // ── Clone section ─────────────────────────────────────────────────────────
  const handleClone = async (targetId: number, sourceId: number) => {
    const res = await cloneSectionTeachers(targetId, sourceId);
    if (res?.success) {
      toastMessage(`Cloned ${res.data?.cloned ?? ''} teacher assignments`, 'success');
      setSidePanel(null);
      if (activeSectionId) await loadSectionSubjects(activeSectionId);
      await onReloadData(true);
    } else {
      toastMessage((res as any)?.message || 'Clone failed', 'error');
    }
  };

  // ── Assign subjects ───────────────────────────────────────────────────────
  const handleAssignMultiple = async (subjectIds: number[]) => {
    const results = await Promise.all(subjectIds.map(id => assignSubjectToClass(selectedClassId, id)));
    const failed = results.filter(r => !(r as any)?.success);
    if (failed.length === 0) toastMessage(subjectIds.length === 1 ? 'Subject assigned' : `${subjectIds.length} subjects assigned`, 'success');
    else toastMessage(`${subjectIds.length - failed.length} assigned, ${failed.length} failed`, 'error');
    await onReloadData(true);
    if (activeSectionId) await loadSectionSubjects(activeSectionId);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const { reqs, done, pct } = computeProgress();
  const isComplete = pct === 100;
  const circumference = 2 * Math.PI * 28;
  const dashOffset = circumference - (pct / 100) * circumference;
  const activeSection = sections.find((s: any) => s.id === activeSectionId);

  const whatsMissing = [
    !reqs[0] && 'Add at least one section',
    !reqs[1] && 'Assign subjects to this class',
    reqs[1] && !reqs[2] && 'Some subjects are missing a teacher in some sections',
    sections.length > 0 && !reqs[3] && 'Assign a class teacher to each section',
  ].filter(Boolean) as string[];

  const stepDone = [reqs[0], reqs[1], reqs[2] && reqs[3], isComplete];
  const activeStep = stepDone.findIndex(d => !d);

  const tableRows: SectionSubjectRow[] = sections.length > 0
    ? sectionSubjects
    : subjectMaps.map((m: any) => ({
        subject_id: m.subject_id_fk,
        subject: m.subject,
        teacher_id: null,
        teacher: null,
        section_map_id: null,
        class_subject_map_id: m.id,
      }));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', background: 'var(--surface)' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>
        <button onClick={closeModal} className="icon-btn" style={{ flexShrink: 0 }}><ChevronLeft size={20} /></button>

        <div style={{ flexShrink: 0 }}>
          {editingWorkspaceClassName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input autoFocus value={workspaceClassNameVal} onChange={e => setWorkspaceClassNameVal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRenameClass(workspaceClassNameVal); if (e.key === 'Escape') setEditingWorkspaceClassName(false); }}
                className="input" style={{ height: 34, fontSize: 14, fontWeight: 700, width: 180 }} />
              <button className="icon-btn" style={{ color: 'var(--success)' }} onClick={() => handleRenameClass(workspaceClassNameVal)}><Check size={14} /></button>
              <button className="icon-btn" onClick={() => setEditingWorkspaceClassName(false)}><X size={14} /></button>
            </div>
          ) : (
            <button onClick={() => { setWorkspaceClassNameVal(selectedClass.name); setEditingWorkspaceClassName(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.4px', color: 'var(--text)', whiteSpace: 'nowrap' }}>{selectedClass.name} Setup</span>
              <Pencil size={14} style={{ color: 'var(--text-3)' }} />
            </button>
          )}
          <p className="meta" style={{ marginTop: 2 }}>
            {activeCurriculum?.name} · {subjectMaps.length} subject{subjectMaps.length !== 1 ? 's' : ''} · {sections.length > 0 ? `${sections.length} section${sections.length !== 1 ? 's' : ''}` : 'no sections yet'}
          </p>
        </div>

        <div style={{ flex: 1 }} />

        <span className={`badge ${isComplete ? 'b-success' : 'b-warning'}`}>
          <span className="dot" />
          {isComplete ? 'Complete' : 'In progress'}
        </span>
        <button onClick={closeModal} className="btn btn-soft">Save draft</button>
        <button onClick={closeModal} className="btn btn-accent">
          <Check size={16} strokeWidth={2.5} /> Finish setup
        </button>
        <button onClick={closeModal} className="icon-btn"><X size={20} /></button>
      </div>

      {/* ── Step bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 24px', borderBottom: '1px solid var(--divider)', background: 'var(--surface-2)', flexShrink: 0 }}>
        <div className="steps">
          {(['Add Section', 'Add Subjects', 'Assign Teachers', 'Review & Finish'] as const).map((label, i) => {
            const d = stepDone[i];
            const active = !d && i === (activeStep === -1 ? 3 : activeStep);
            return (
              <React.Fragment key={label}>
                <div className={`step${d ? ' done' : active ? ' active' : ''}`}>
                  <div className="step-dot">{d ? <Check size={11} strokeWidth={3} /> : i + 1}</div>
                  <span className="step-label">{label}</span>
                </div>
                {i < 3 && <div className="step-line" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* Left: content cards */}
        <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
          <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* ── Card 1: Subjects & Teachers ── */}
            <div className="card" style={{ overflow: 'visible' }}>
              <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px', color: 'var(--text)' }}>
                  Subjects & Teachers
                  {activeSection && <span style={{ fontWeight: 600, color: 'var(--text-3)', fontSize: 14 }}> — Section {activeSection.section_label}</span>}
                </h3>
                <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-2)' }}>
                  {sections.length > 0 ? 'Select a section tab to view & assign teachers' : 'Add sections first to enable per-section teacher assignments'}
                </p>
              </div>

              {/* Section tabs */}
              {sections.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 20px 14px', paddingTop: 14, borderBottom: '1px solid var(--divider)', overflowX: 'auto' }}>
                  {sections.map((sec: any) => (
                    <div key={sec.id} style={{ flexShrink: 0 }}>
                      {editingSectionId === sec.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input autoFocus value={editingSectionLabel} onChange={e => setEditingSectionLabel(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleRenameSection(sec.id, editingSectionLabel); if (e.key === 'Escape') setEditingSectionId(null); }}
                            className="input" style={{ height: 34, width: 80, fontSize: 13 }} />
                          <button className="icon-btn" style={{ color: 'var(--success)', width: 28, height: 28 }} onClick={() => handleRenameSection(sec.id, editingSectionLabel)}><Check size={12} /></button>
                          <button className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => setEditingSectionId(null)}><X size={12} /></button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <button onClick={() => { setActiveSectionId(sec.id); loadSectionSubjects(sec.id); }}
                            className="chip"
                            style={{
                              height: 34, minWidth: 34, padding: '0 12px', justifyContent: 'center', cursor: 'pointer',
                              background: activeSectionId === sec.id ? 'var(--slate)' : 'var(--surface)',
                              color: activeSectionId === sec.id ? '#fff' : 'var(--text-2)',
                              border: activeSectionId === sec.id ? 'none' : '1.5px solid var(--border-2)',
                              position: 'relative', fontWeight: 700, fontSize: 13,
                            }}>
                            {sec.section_label}
                            <span style={{
                              position: 'absolute', top: -3, right: -3, width: 8, height: 8, borderRadius: '50%', border: '1.5px solid var(--surface)',
                              background: sec.primary_teacher_id && sec.PaSectionSubjectMaps?.length >= subjectMaps.length && sec.PaSectionSubjectMaps?.every((m: any) => m.teacher_id_fk) ? 'var(--success)' : 'var(--warning)',
                            }} />
                          </button>
                          {activeSectionId === sec.id && (
                            <>
                              <button className="icon-btn" style={{ width: 24, height: 24, borderRadius: 6 }} onClick={e => { e.stopPropagation(); setEditingSectionLabel(sec.section_label); setEditingSectionId(sec.id); }}><Pencil size={11} /></button>
                              <button className="icon-btn" style={{ width: 24, height: 24, borderRadius: 6, color: 'var(--danger)' }} onClick={e => { e.stopPropagation(); handleDeleteSection(sec.id); }}><Trash2 size={11} /></button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  <button onClick={() => setSidePanel({ type: 'addSection' })}
                    className="chip" style={{ height: 34, padding: '0 12px', cursor: 'pointer', background: 'var(--surface)', border: '1.5px dashed var(--border-2)', color: 'var(--text-3)', flexShrink: 0 }}>
                    <Plus size={14} /> Add Section
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', background: 'var(--warning-wash)', borderBottom: '1px solid var(--warning-soft)' }}>
                  <AlertCircle size={15} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--warning)' }}>
                    No sections yet.{' '}
                    <button onClick={() => setSidePanel({ type: 'addSection' })} style={{ background: 'none', border: 'none', color: 'var(--warning)', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 13 }}>
                      Add a section
                    </button>
                    {' '}to assign teachers per section.
                  </p>
                </div>
              )}

              {/* Subjects table */}
              {subjectMaps.length === 0 ? (
                <div className="empty" style={{ padding: '36px 24px' }}>
                  <div className="empty-art" style={{ width: 56, height: 56, borderRadius: 16 }}><BookMarked size={24} /></div>
                  <h3 style={{ fontSize: 15 }}>No subjects yet</h3>
                  <p>Add subjects to this class first</p>
                  <button className="btn btn-primary btn-sm" onClick={() => setSidePanel({ type: 'addSubject' })}><Plus size={14} /> Add Subject</button>
                </div>
              ) : (
                <>
                  {loadingSectionSubjects ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                      <Loader2 size={20} style={{ animation: 'pa-spin .7s linear infinite', color: 'var(--text-3)' }} />
                    </div>
                  ) : (
                    <table className="tbl">
                      <thead><tr><th>Subject</th><th>Teacher</th><th>Status</th><th style={{ width: 44 }}></th></tr></thead>
                      <tbody>
                        {tableRows.map((row, idx) => {
                          const cat = CARD_CATS[idx % CARD_CATS.length];
                          const isSaving = savingTeacher === `ssm-${activeSectionId}-${row.subject_id}`;
                          return (
                            <tr key={row.subject_id}>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div className={cat} style={{ width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                                    {row.subject?.name?.[0]?.toUpperCase()}
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{row.subject?.name}</div>
                                    {row.subject?.category && <span className={`chip chip-sm ${CARD_CATS[idx % CARD_CATS.length]}`} style={{ marginTop: 3 }}>{row.subject.category}</span>}
                                  </div>
                                </div>
                              </td>
                              <td>
                                {row.teacher ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {row.teacher.photo_url
                                      ? <img src={row.teacher.photo_url} alt={row.teacher.name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                      : <div className="avatar cat-blue" style={{ width: 28, height: 28, fontSize: 11 }}>{row.teacher.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}</div>
                                    }
                                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>{row.teacher.name}</span>
                                  </div>
                                ) : <span style={{ color: 'var(--text-3)', fontSize: 13.5 }}>Unassigned</span>}
                              </td>
                              <td>
                                {row.teacher_id
                                  ? <span className="badge b-success"><span className="dot" />Assigned</span>
                                  : <span className="badge b-warning"><span className="dot" />Pending</span>}
                              </td>
                              <td style={{ position: 'relative', textAlign: 'right' }}>
                                {isSaving
                                  ? <Loader2 size={15} style={{ animation: 'pa-spin .7s linear infinite', color: 'var(--text-3)' }} />
                                  : <button className="icon-btn"
                                      onClick={e => { e.stopPropagation(); const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); if (openKebab === row.subject_id) { setOpenKebab(null); setOpenKebabPos(null); } else { const spaceBelow = window.innerHeight - r.bottom; setOpenKebab(row.subject_id); setOpenKebabPos(spaceBelow > 100 ? { top: r.bottom + 4, right: window.innerWidth - r.right } : { bottom: window.innerHeight - r.top + 4, right: window.innerWidth - r.right }); } }}>
                                      <MoreVertical size={16} />
                                    </button>
                                }
                                {openKebab === row.subject_id && openKebabPos && (
                                  <>
                                    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={() => { setOpenKebab(null); setOpenKebabPos(null); }} />
                                    <div className="card" style={{ position: 'fixed', zIndex: 70, width: 160, padding: 6, boxShadow: 'var(--sh-lg)', top: openKebabPos.top, bottom: openKebabPos.bottom, right: openKebabPos.right }}>
                                      <button onClick={() => { setOpenKebab(null); setOpenKebabPos(null); setSidePanel({ type: 'assignTeacher', sectionId: activeSectionId!, subjectId: row.subject_id, subjectName: row.subject?.name ?? '', currentTeacherId: row.teacher_id }); }}
                                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, border: 'none', cursor: 'pointer', background: 'transparent', fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}
                                        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-tint)'}
                                        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                                        <Check size={14} style={{ color: 'var(--success)' }} />
                                        {row.teacher_id ? 'Change teacher' : 'Assign teacher'}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>

            {/* ── Card 2: Class Teachers ── */}
            {sections.length > 0 && (
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px', color: 'var(--text)' }}>Class Teachers</h3>
                    <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-2)' }}>Assign primary and support class teachers per section</p>
                  </div>
                  {sections.length > 1 && (
                    <button className="btn btn-soft btn-sm"
                      onClick={() => setSidePanel({ type: 'cloneSection', targetSectionId: activeSectionId ?? sections[0].id, targetLabel: activeSection?.section_label ?? sections[0].section_label })}>
                      <Copy size={13} /> Clone Assignments
                    </button>
                  )}
                </div>
                <table className="tbl">
                  <thead><tr><th>Section</th><th>Primary Teacher</th><th>Support Teacher</th><th style={{ width: 44 }}></th></tr></thead>
                  <tbody>
                    {sections.map((sec: any) => {
                      const pt = teachers.find(t => t.id === sec.primary_teacher_id);
                      const st = teachers.find(t => t.id === sec.secondary_teacher_id);
                      const isSavingPrimary = savingTeacher === `ct-${sec.id}-primary`;
                      const isSavingSecondary = savingTeacher === `ct-${sec.id}-secondary`;
                      return (
                        <tr key={sec.id}>
                          <td><span className="chip cat-indigo chip-sm">{sec.section_label}</span></td>
                          <td>
                            {isSavingPrimary
                              ? <Loader2 size={15} style={{ animation: 'pa-spin .7s linear infinite', color: 'var(--text-3)' }} />
                              : pt ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {pt.photo_url ? <img src={pt.photo_url} alt={pt.name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} /> : <div className="avatar cat-blue" style={{ width: 28, height: 28, fontSize: 11 }}>{pt.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}</div>}
                                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>{pt.name}</span>
                                </div>
                              ) : <span style={{ color: 'var(--text-3)', fontSize: 13.5 }}>+ Add primary</span>}
                          </td>
                          <td>
                            {isSavingSecondary
                              ? <Loader2 size={15} style={{ animation: 'pa-spin .7s linear infinite', color: 'var(--text-3)' }} />
                              : st ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {st.photo_url ? <img src={st.photo_url} alt={st.name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} /> : <div className="avatar cat-purple" style={{ width: 28, height: 28, fontSize: 11 }}>{st.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}</div>}
                                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>{st.name}</span>
                                </div>
                              ) : <span style={{ color: 'var(--text-3)', fontSize: 13.5 }}>+ Add support</span>}
                          </td>
                          <td style={{ position: 'relative', textAlign: 'right' }}>
                            <button className="icon-btn"
                              onClick={e => { e.stopPropagation(); const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); if (openCtKebab === sec.id) { setOpenCtKebab(null); setOpenCtKebabPos(null); } else { const spaceBelow = window.innerHeight - r.bottom; setOpenCtKebab(sec.id); setOpenCtKebabPos(spaceBelow > 100 ? { top: r.bottom + 4, right: window.innerWidth - r.right } : { bottom: window.innerHeight - r.top + 4, right: window.innerWidth - r.right }); } }}>
                              <MoreVertical size={16} />
                            </button>
                            {openCtKebab === sec.id && openCtKebabPos && (
                              <>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={() => { setOpenCtKebab(null); setOpenCtKebabPos(null); }} />
                                <div className="card" style={{ position: 'fixed', zIndex: 70, width: 180, padding: 6, boxShadow: 'var(--sh-lg)', top: openCtKebabPos.top, bottom: openCtKebabPos.bottom, right: openCtKebabPos.right }}>
                                  {[{ role: 'primary' as const, label: sec.primary_teacher_id ? 'Change Primary' : 'Assign Primary' }, { role: 'secondary' as const, label: sec.secondary_teacher_id ? 'Change Support' : 'Assign Support' }].map(item => (
                                    <button key={item.role}
                                      onClick={() => { setOpenCtKebab(null); setSidePanel({ type: 'classTeacher', section: sec, role: item.role }); }}
                                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, border: 'none', cursor: 'pointer', background: 'transparent', fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}
                                      onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-tint)'}
                                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                                      <Check size={14} style={{ color: item.role === 'primary' ? 'var(--success)' : 'var(--c-purple)' }} />
                                      {item.label}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

          </div>

          {/* ── Side Drawers ── */}
          {sidePanel?.type === 'addSection' && (
            <SideDrawer title="Add Section" onClose={() => { setSidePanel(null); setNewSectionLabel(''); setCloneFromSectionId(null); }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-2)', marginBottom: 6 }}>Section Label</label>
                  <input autoFocus value={newSectionLabel} onChange={e => setNewSectionLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && newSectionLabel.trim()) handleAddSection(); if (e.key === 'Escape') { setSidePanel(null); setNewSectionLabel(''); setCloneFromSectionId(null); } }}
                    placeholder="e.g. A, B, C or Morning, Evening"
                    className="input" style={{ width: '100%' }} />
                </div>
                {sections.length > 0 && (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-2)', marginBottom: 4 }}>
                      Copy setup from an existing section? <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(optional)</span>
                    </label>
                    <p style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>Subjects, teacher assignments, and class teachers will be copied.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button onClick={() => setCloneFromSectionId(null)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${cloneFromSectionId === null ? 'var(--accent-deep)' : 'var(--border)'}`, background: cloneFromSectionId === null ? 'var(--accent-wash)' : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${cloneFromSectionId === null ? 'var(--accent-deep)' : 'var(--border-2)'}`, background: cloneFromSectionId === null ? 'var(--accent-deep)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {cloneFromSectionId === null && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                        </div>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>Start blank</span>
                      </button>
                      {sections.map((sec: any) => (
                        <button key={sec.id} onClick={() => setCloneFromSectionId(sec.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${cloneFromSectionId === sec.id ? 'var(--accent-deep)' : 'var(--border)'}`, background: cloneFromSectionId === sec.id ? 'var(--accent-wash)' : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                          <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${cloneFromSectionId === sec.id ? 'var(--accent-deep)' : 'var(--border-2)'}`, background: cloneFromSectionId === sec.id ? 'var(--accent-deep)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {cloneFromSectionId === sec.id && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>Copy from {sec.section_label}</p>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
                              {sec.PaSectionSubjectMaps?.length ?? 0} subject assignment{(sec.PaSectionSubjectMaps?.length ?? 0) !== 1 ? 's' : ''}
                              {sec.primary_teacher_id ? ' · class teacher set' : ''}
                            </p>
                          </div>
                          <Copy size={14} style={{ flexShrink: 0, color: 'var(--text-3)' }} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setSidePanel(null); setNewSectionLabel(''); setCloneFromSectionId(null); }} className="btn btn-soft" style={{ flex: 1 }}>Cancel</button>
                  <button onClick={handleAddSection} disabled={!newSectionLabel.trim()} className="btn btn-primary" style={{ flex: 1 }}>
                    {cloneFromSectionId ? 'Add & Clone' : 'Add Section'}
                  </button>
                </div>
              </div>
            </SideDrawer>
          )}

          {sidePanel?.type === 'addSubject' && (
            <SideDrawer title="Add Subjects" onClose={() => setSidePanel(null)}>
              <SubjectPickerPanel
                subjectMaster={subjectMaster}
                assignedSubjectIds={subjectMaps.map((m: any) => m.subject_id_fk)}
                onAssignMultiple={handleAssignMultiple}
                onClose={() => setSidePanel(null)}
              />
            </SideDrawer>
          )}

          {sidePanel?.type === 'assignTeacher' && (
            <SideDrawer title={`Assign Teacher — ${sidePanel.subjectName}`} onClose={() => setSidePanel(null)}>
              <TeacherPickerList
                teachers={teachers}
                currentId={sidePanel.currentTeacherId}
                saving={savingTeacher === `ssm-${sidePanel.sectionId}-${sidePanel.subjectId}`}
                onSelect={id => handleAssignSectionTeacher(sidePanel.subjectId, id)}
              />
            </SideDrawer>
          )}

          {sidePanel?.type === 'classTeacher' && (
            <SideDrawer
              title={`${sidePanel.role === 'primary' ? 'Primary' : 'Support'} Teacher — ${sidePanel.section.section_label}`}
              onClose={() => setSidePanel(null)}>
              <TeacherPickerList
                teachers={teachers}
                currentId={sidePanel.role === 'primary' ? (sidePanel.section.primary_teacher_id ?? null) : (sidePanel.section.secondary_teacher_id ?? null)}
                excludeId={sidePanel.role === 'primary' ? sidePanel.section.secondary_teacher_id : sidePanel.section.primary_teacher_id}
                saving={savingTeacher === `ct-${sidePanel.section.id}-${sidePanel.role}`}
                onSelect={id => handleSectionClassTeacher(sidePanel.section, id, sidePanel.role)}
              />
            </SideDrawer>
          )}

          {sidePanel?.type === 'loadTemplate' && (
            <SideDrawer title={`Load Template — ${sidePanel.gradeLabel}`} onClose={() => setSidePanel(null)}>
              <TemplatePickerPanel
                boardId={sidePanel.boardId}
                gradeLabel={sidePanel.gradeLabel}
                classId={sidePanel.classId}
                onApplied={async () => {
                  await onReloadData(true);
                  await onReloadSubjectMaster();
                  if (activeSectionId) await loadSectionSubjects(activeSectionId);
                }}
                onClose={() => setSidePanel(null)}
              />
            </SideDrawer>
          )}

          {sidePanel?.type === 'cloneSection' && (
            <SideDrawer title={`Clone to Section ${sidePanel.targetLabel}`} onClose={() => setSidePanel(null)}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>Copy all teacher assignments from another section into Section {sidePanel.targetLabel}. Existing assignments will be overwritten.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sections.filter((s: any) => s.id !== sidePanel.targetSectionId).map((sec: any) => (
                    <button key={sec.id} onClick={() => handleClone(sidePanel.targetSectionId, sec.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-deep)'; (e.currentTarget as HTMLElement).style.background = 'var(--accent-wash)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                      <Copy size={15} style={{ color: 'var(--accent-deep)', flexShrink: 0 }} />
                      <div>
                        <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>Copy from {sec.section_label}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
                          {sec.PaSectionSubjectMaps?.filter((m: any) => m.teacher_id_fk)?.length ?? 0} assignment{(sec.PaSectionSubjectMaps?.filter((m: any) => m.teacher_id_fk)?.length ?? 0) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </SideDrawer>
          )}
        </div>

        {/* ── Right Sidebar ── */}
        <div style={{ width: 340, flexShrink: 0, borderLeft: '1px solid var(--border)', background: 'var(--surface-2)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

          {/* Progress donut */}
          <div style={{ padding: '26px 22px', borderBottom: '1px solid var(--divider)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <p className="meta" style={{ fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 16 }}>Setup Progress</p>
            <div style={{ position: 'relative', width: 90, height: 90, marginBottom: 10 }}>
              <svg style={{ width: 90, height: 90, transform: 'rotate(-90deg)' }} viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" stroke="var(--border-2)" strokeWidth="5" />
                <circle cx="32" cy="32" r="28" fill="none" strokeWidth="5"
                  stroke={isComplete ? 'var(--success)' : 'var(--accent-deep)'}
                  strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{pct}%</span>
              </div>
            </div>
            <p className="meta">{done} of {reqs.length} steps complete</p>
          </div>

          {/* Summary */}
          <div style={{ padding: '26px 22px', borderBottom: '1px solid var(--divider)' }}>
            <p className="meta" style={{ fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 14 }}>Summary</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Class', value: selectedClass.name },
                { label: 'Sections', value: sections.length || '—' },
                { label: 'Subjects', value: subjectMaps.length || '—' },
                { label: 'Teachers Assigned', value: sections.reduce((acc: number, sec: any) => acc + (sec.PaSectionSubjectMaps?.filter((m: any) => m.teacher_id_fk)?.length ?? 0), 0) || '—' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span className="meta">{item.label}</span>
                  <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* What's Missing */}
          <div style={{ padding: '26px 22px', flex: 1 }}>
            <p className="meta" style={{ fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 14 }}>What's Missing</p>
            {whatsMissing.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--success-wash)', borderRadius: 10 }}>
                <CheckCircle2 size={15} style={{ color: 'var(--success)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>Class setup complete!</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {whatsMissing.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <AlertCircle size={14} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 13, color: 'var(--warning)', lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </div>
            )}

            {sections.length > 1 && (
              <div style={{ marginTop: 24 }}>
                <p className="meta" style={{ fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 10 }}>Quick Actions</p>
                <button className="btn btn-soft" style={{ width: '100%', justifyContent: 'flex-start' }}
                  onClick={() => setSidePanel({ type: 'cloneSection', targetSectionId: activeSectionId ?? sections[0].id, targetLabel: activeSection?.section_label ?? sections[0].section_label })}>
                  <Copy size={13} />
                  Clone section teachers
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
